/*
 * franzi-gallery-crop.js
 * ------------------------------------------------------------------
 * PDP gallery auto-crop (issue #143). Packshots carry large white
 * margins (tuned so shop-card thumbnails line up), so inside the fixed
 * 654/820 gallery frames the product reads small and pushed down. This
 * module finds the subject's bounding box in the browser — a tiny CDN
 * rendition drawn onto a canvas (cdn.shopify.com sends
 * `access-control-allow-origin: *`) — and reframes the <img> inside
 * its figure with percentage left/top/width/height, adding uniform
 * padding around the subject.
 *
 * Reflow safety: the figure keeps its aspect-ratio box, so applying a
 * crop never shifts layout; it only repaints inside the frame. To
 * avoid a visible reframe on first view, unmeasured images hold at
 * opacity 0 (.is-measuring) until their box is known, then fade in.
 * Boxes are cached in localStorage, so subsequent views apply the crop
 * synchronously before the image ever paints.
 *
 * Full-bleed photos (details, worn shots) have subjects touching the
 * image edges; they are detected and left untouched, as are images
 * whose scan fails (they simply reveal uncropped).
 * ------------------------------------------------------------------
 */
(function () {
  "use strict";

  var PROBE_WIDTH = 128; // CDN rendition used for the pixel scan
  var WHITE_THRESHOLD = 12; // luma distance from pure white (0-255)
  var EDGE_MARGIN = 0.02; // subject nearer than this to any edge = full-bleed
  var MIN_SUBJECT = 0.05; // ignore boxes smaller than 5% per axis (noise)
  var PADDING = 0.08; // breathing room added around the subject box
  var FRAME_RATIO = 654 / 820; // matches .product-gallery__item CSS
  var REVEAL_TIMEOUT_MS = 5000; // never hold an image hidden longer than this
  var CACHE_PREFIX = "franziGalleryCrop:v1:";

  var memoryCache = new Map();

  function cacheKeyFor(src) {
    try {
      var url = new URL(src, window.location.href);
      var basename = url.pathname.split("/").pop();
      if (!basename) return "";
      var version = url.searchParams.get("v") || "";
      return CACHE_PREFIX + basename + "?v=" + version;
    } catch {
      return "";
    }
  }

  function readCache(key) {
    if (memoryCache.has(key)) return memoryCache.get(key);
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.nr !== "number") return null;
      return entry;
    } catch {
      return null;
    }
  }

  function writeCache(key, entry) {
    memoryCache.set(key, entry);
    try {
      window.localStorage.setItem(key, JSON.stringify(entry));
    } catch {
      /* quota/private mode: memory cache still covers this page */
    }
  }

  /*
   * Scan a probe rendition for the subject bounding box. Resolves
   * { box: [l, t, r, b] | null, nr } with coordinates normalized to the
   * image (box null = leave the image uncropped); rejects on load or
   * canvas errors so failures are revealed uncropped and never cached.
   */
  function probe(src) {
    return new Promise(function (resolve, reject) {
      var url;
      try {
        url = new URL(src, window.location.href);
      } catch (error) {
        reject(error);
        return;
      }
      url.searchParams.set("width", String(PROBE_WIDTH));
      var image = new Image();
      image.crossOrigin = "anonymous";
      image.decoding = "async";
      image.onload = function () {
        try {
          resolve(scan(image));
        } catch (error) {
          reject(error);
        }
      };
      image.onerror = function () {
        reject(new Error("franzi-gallery-crop: probe failed " + url));
      };
      image.src = url.toString();
    });
  }

  function scan(image) {
    var width = image.naturalWidth;
    var height = image.naturalHeight;
    if (!width || !height) throw new Error("franzi-gallery-crop: empty probe");
    var canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    var context = canvas.getContext("2d", { willReadFrequently: true });
    // Composite onto white so transparency counts as background.
    context.fillStyle = "#fff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0);
    var data = context.getImageData(0, 0, width, height).data;

    var minX = width;
    var minY = height;
    var maxX = -1;
    var maxY = -1;
    for (var y = 0; y < height; y += 1) {
      for (var x = 0; x < width; x += 1) {
        var i = (y * width + x) * 4;
        var luma =
          0.299 * (255 - data[i]) +
          0.587 * (255 - data[i + 1]) +
          0.114 * (255 - data[i + 2]);
        if (luma <= WHITE_THRESHOLD) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }

    var naturalRatio = Math.round((height / width) * 10000) / 10000;
    var none = { box: null, nr: naturalRatio };
    if (maxX < 0) return none;

    var box = [
      minX / width,
      minY / height,
      (maxX + 1) / width,
      (maxY + 1) / height,
    ].map(function (value) {
      return Math.round(value * 10000) / 10000;
    });

    // Subjects touching any edge are full-bleed photography, not packshots.
    if (
      box[0] <= EDGE_MARGIN ||
      box[1] <= EDGE_MARGIN ||
      box[2] >= 1 - EDGE_MARGIN ||
      box[3] >= 1 - EDGE_MARGIN
    ) {
      return none;
    }
    if (box[2] - box[0] < MIN_SUBJECT || box[3] - box[1] < MIN_SUBJECT) {
      return none;
    }
    return { box: box, nr: naturalRatio };
  }

  /*
   * Reframe the <img> so the padded subject box fills its fixed-aspect
   * figure. Works in image-pixel space (width 1, height nr), expands the
   * box to the frame's aspect ratio plus PADDING — the expanded box may
   * extend past the image, which just shows the white figure background —
   * then converts to percentage offsets relative to the figure.
   *
   * The math always targets the design 654/820 frame, never the live
   * rect: at apply time figures sit in unequal CSS states (the first
   * frame is already viewport-clamped, later frames clamp only after
   * this function adds the cropped class), so measuring produced a
   * different reframe per slide. With a constant ratio every slide gets
   * identical styles and object-fit: contain absorbs the viewport clamp.
   */
  function applyCrop(img, entry) {
    if (!entry || !entry.box) return;
    var box = entry.box;
    var naturalRatio = entry.nr;
    var frameRatio = FRAME_RATIO;

    var x = box[0];
    var y = box[1] * naturalRatio;
    var w = box[2] - box[0];
    var h = (box[3] - box[1]) * naturalRatio;

    if (w / h < frameRatio) {
      var targetW = h * frameRatio;
      x -= (targetW - w) / 2;
      w = targetW;
    } else {
      var targetH = w / frameRatio;
      y -= (targetH - h) / 2;
      h = targetH;
    }
    x -= w * PADDING;
    y -= h * PADDING;
    w *= 1 + 2 * PADDING;
    h *= 1 + 2 * PADDING;

    var cropLeft = x;
    var cropTop = y / naturalRatio;
    var cropWidth = w;

    var widthPct = 100 / cropWidth;
    var heightPct = widthPct * naturalRatio * frameRatio;
    img.style.width = widthPct.toFixed(3) + "%";
    img.style.height = heightPct.toFixed(3) + "%";
    img.style.left = (-cropLeft * widthPct).toFixed(3) + "%";
    img.style.top = (-cropTop * heightPct).toFixed(3) + "%";
    img.classList.add("product-gallery__image--cropped");
  }

  /*
   * Cropped desktop frames size themselves to the viewport:
   * calc(100svh - var(--product-gallery-offset-top)). Publish the
   * gallery's document offset as that custom property, debounced on
   * resize.
   */
  function initOffsetVar() {
    var gallery = document.querySelector(".product-gallery");
    if (!gallery) return;

    function setOffset() {
      var top =
        gallery.getBoundingClientRect().top +
        (window.scrollY || window.pageYOffset || 0);
      gallery.style.setProperty(
        "--product-gallery-offset-top",
        Math.max(0, Math.round(top)) + "px",
      );
    }

    setOffset();
    var debounce;
    window.addEventListener("resize", function () {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(setOffset, 150);
    });
  }

  function init() {
    var images = Array.prototype.slice.call(
      document.querySelectorAll(".product-gallery__image"),
    );
    if (!images.length) return;

    var pending = [];
    images.forEach(function (img) {
      var key = cacheKeyFor(img.src);
      if (!key) return;
      var cached = readCache(key);
      if (cached) {
        // Cache hit: reframe synchronously, before the image first paints.
        applyCrop(img, cached);
        return;
      }
      img.classList.add("is-measuring");
      pending.push(img);
    });
    if (!pending.length) return;

    var inFlight = new Map();

    function measure(img) {
      var key = cacheKeyFor(img.src);
      var promise = inFlight.get(key);
      if (!promise) {
        promise = probe(img.src);
        inFlight.set(key, promise);
      }
      var revealTimer = window.setTimeout(function () {
        img.classList.remove("is-measuring");
      }, REVEAL_TIMEOUT_MS);
      promise
        .then(function (entry) {
          writeCache(key, entry);
          applyCrop(img, entry);
        })
        .catch(function () {
          /* probe failed: reveal uncropped, do not cache */
        })
        .finally(function () {
          window.clearTimeout(revealTimer);
          img.classList.remove("is-measuring");
        });
    }

    // The active variant set measures eagerly: its frames are (or may
    // scroll) above the fold, and IntersectionObserver never fires in
    // hidden tabs while probes and canvases still work there. Hidden
    // sets measure lazily on reveal; inFlight dedupes shared files.
    var eager = [];
    var lazy = [];
    pending.forEach(function (img) {
      var variantSet = img.closest("[data-franzi-media-set]");
      (variantSet && variantSet.hidden ? lazy : eager).push(img);
    });
    eager.forEach(measure);
    if (!lazy.length) return;

    if ("IntersectionObserver" in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (observed) {
            if (!observed.isIntersecting) return;
            observer.unobserve(observed.target);
            measure(observed.target);
          });
        },
        { rootMargin: "50% 0px" },
      );
      lazy.forEach(function (img) {
        observer.observe(img);
      });
    } else {
      lazy.forEach(measure);
    }
  }

  initOffsetVar();
  init();
})();
